import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../../css/ArchiveLibrary.css';

const ArchiveLibrary = ({ boardId, onClose, onUnarchive }) => {
    const { apiCall } = useAuth();
    const [activeTab, setActiveTab] = useState('singles');
    const [loading, setLoading] = useState(true);
    const [singles, setSingles] = useState([]);
    const [labelGroups, setLabelGroups] = useState([]);
    const [customGroups, setCustomGroups] = useState([]);
    const [totalArchived, setTotalArchived] = useState(0);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [groupTasks, setGroupTasks] = useState([]);
    const [groupTasksLoading, setGroupTasksLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [unarchiving, setUnarchiving] = useState(false);
    const [deletingGroup, setDeletingGroup] = useState(null);
    const [viewingTask, setViewingTask] = useState(null);
    const searchTimer = useRef(null);

    const fetchArchive = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/archive/`);
            if (response.ok) {
                const data = await response.json();
                setSingles(data.singles);
                setLabelGroups(data.label_groups);
                setCustomGroups(data.custom_groups);
                setTotalArchived(data.total_archived);
            }
        } catch (err) {
            console.error('Error fetching archive:', err);
        }
        setLoading(false);
    }, [apiCall, boardId]);

    useEffect(() => {
        fetchArchive();
    }, [fetchArchive]);

    const fetchGroupTasks = async (groupId) => {
        if (expandedGroup === groupId) {
            setExpandedGroup(null);
            setGroupTasks([]);
            return;
        }
        setGroupTasksLoading(true);
        setExpandedGroup(groupId);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/archive/groups/${groupId}/`);
            if (response.ok) {
                const data = await response.json();
                setGroupTasks(data.tasks);
            }
        } catch (err) {
            console.error('Error fetching group tasks:', err);
        }
        setGroupTasksLoading(false);
    };

    const handleSearch = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/archive/search/?q=${encodeURIComponent(query)}`);
            if (response.ok) {
                const data = await response.json();
                setSearchResults(data);
            }
        } catch (err) {
            console.error('Error searching archive:', err);
        }
        setSearching(false);
    }, [apiCall, boardId]);

    const handleSearchInput = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => handleSearch(val), 300);
    };

    const toggleTaskSelection = (taskId) => {
        setSelectedTasks(prev =>
            prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
        );
    };

    const handleUnarchive = async () => {
        if (selectedTasks.length === 0) return;
        setUnarchiving(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/unarchive/`, {
                method: 'POST',
                body: JSON.stringify({ task_ids: selectedTasks }),
            });
            if (response.ok) {
                setSelectedTasks([]);
                await fetchArchive();
                setExpandedGroup(null);
                setGroupTasks([]);
                if (onUnarchive) onUnarchive();
            }
        } catch (err) {
            console.error('Error unarchiving tasks:', err);
        }
        setUnarchiving(false);
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Permanently delete this archive group and all its tasks? This cannot be undone.')) return;
        setDeletingGroup(groupId);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/archive/groups/${groupId}/delete/`, {
                method: 'DELETE',
            });
            if (response.ok) {
                setExpandedGroup(null);
                setGroupTasks([]);
                setSelectedTasks([]);
                await fetchArchive();
                if (onUnarchive) onUnarchive();
            }
        } catch (err) {
            console.error('Error deleting group:', err);
        }
        setDeletingGroup(null);
    };

    const isExpanded = (taskId) => viewingTask === taskId;

    const renderTaskItem = (task) => (
        <div key={task.id} className="ArchiveLibrary__task-wrapper">
            <div className="ArchiveLibrary__task-item">
                <input
                    type="checkbox"
                    checked={selectedTasks.includes(task.id)}
                    onChange={() => toggleTaskSelection(task.id)}
                />
                <div
                    className="ArchiveLibrary__task-info"
                    onClick={() => setViewingTask(isExpanded(task.id) ? null : task.id)}
                    role="button"
                    tabIndex={0}
                >
                    <span className="ArchiveLibrary__task-id">{task.board_task_id}</span>
                    <span className="ArchiveLibrary__task-title">{task.title}</span>
                    <span className="ArchiveLibrary__task-meta">
                        {task.column_name}
                        {task.priority && task.priority !== 'NONE' && (
                            <span className={`ArchiveLibrary__priority ArchiveLibrary__priority--${task.priority.toLowerCase()}`}>
                                {task.priority}
                            </span>
                        )}
                    </span>
                    {task.labels && task.labels.length > 0 && (
                        <div className="ArchiveLibrary__task-labels">
                            {task.labels.map((l, i) => (
                                <span key={i} className="ArchiveLibrary__label-chip">{l}</span>
                            ))}
                        </div>
                    )}
                    <i className={`fas fa-chevron-${isExpanded(task.id) ? 'up' : 'down'} ArchiveLibrary__task-expand-icon`} />
                </div>
            </div>
            {isExpanded(task.id) && (
                <div className="ArchiveLibrary__task-detail">
                    <div className="ArchiveLibrary__detail-row">
                        <span className="ArchiveLibrary__detail-label">Description</span>
                        <div className="ArchiveLibrary__detail-value ArchiveLibrary__detail-description">
                            {task.description || 'No description'}
                        </div>
                    </div>
                    <div className="ArchiveLibrary__detail-grid">
                        <div className="ArchiveLibrary__detail-row">
                            <span className="ArchiveLibrary__detail-label">Priority</span>
                            <span className="ArchiveLibrary__detail-value">
                                {task.priority === 'NONE' ? 'None' : task.priority}
                            </span>
                        </div>
                        <div className="ArchiveLibrary__detail-row">
                            <span className="ArchiveLibrary__detail-label">Column</span>
                            <span className="ArchiveLibrary__detail-value">{task.column_name}</span>
                        </div>
                        {task.due_date && (
                            <div className="ArchiveLibrary__detail-row">
                                <span className="ArchiveLibrary__detail-label">Due Date</span>
                                <span className="ArchiveLibrary__detail-value">
                                    {new Date(task.due_date).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                        {task.assignee_info && (
                            <div className="ArchiveLibrary__detail-row">
                                <span className="ArchiveLibrary__detail-label">Assignee</span>
                                <span className="ArchiveLibrary__detail-value">
                                    {task.assignee_info.name || task.assignee_info.email}
                                </span>
                            </div>
                        )}
                        {task.archive_group_name && (
                            <div className="ArchiveLibrary__detail-row">
                                <span className="ArchiveLibrary__detail-label">Archive Group</span>
                                <span className="ArchiveLibrary__detail-value">
                                    {task.archive_group_name} ({task.archive_group_type})
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="ArchiveLibrary__detail-timestamps">
                        <span>Created {new Date(task.created_at).toLocaleDateString()}</span>
                        <span>Updated {new Date(task.updated_at).toLocaleDateString()}</span>
                    </div>
                </div>
            )}
        </div>
    );

    const renderGroupItem = (group) => (
        <div key={group.id} className="ArchiveLibrary__group">
            <div className="ArchiveLibrary__group-header" onClick={() => fetchGroupTasks(group.id)}>
                <i className={`fas fa-chevron-${expandedGroup === group.id ? 'down' : 'right'} ArchiveLibrary__group-chevron`} />
                <span className="ArchiveLibrary__group-name">{group.name}</span>
                <span className="ArchiveLibrary__group-count">{group.task_count}</span>
                <button
                    className="ArchiveLibrary__group-delete"
                    onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                    disabled={deletingGroup === group.id}
                    title="Delete group and all tasks"
                >
                    {deletingGroup === group.id
                        ? <span className="btn-spinner btn-spinner--danger" />
                        : <i className="fas fa-trash-alt" />}
                </button>
            </div>
            {expandedGroup === group.id && (
                <div className="ArchiveLibrary__group-tasks">
                    {groupTasksLoading ? (
                        <div className="ArchiveLibrary__loading-inline">
                            <span className="btn-spinner btn-spinner--dark" /> Loading...
                        </div>
                    ) : groupTasks.length === 0 ? (
                        <div className="ArchiveLibrary__empty-hint">No tasks in this group</div>
                    ) : (
                        groupTasks.map(renderTaskItem)
                    )}
                </div>
            )}
        </div>
    );

    const displayedTasks = searchQuery.length >= 2 ? searchResults : null;

    return (
        <div className="ArchiveLibrary__overlay" onClick={onClose}>
            <div className="ArchiveLibrary" onClick={(e) => e.stopPropagation()}>
                <div className="ArchiveLibrary__header">
                    <div className="ArchiveLibrary__header-left">
                        <i className="fas fa-archive" />
                        <h3 className="ArchiveLibrary__title">Archive Library</h3>
                        <span className="ArchiveLibrary__total-badge">{totalArchived}</span>
                    </div>
                    <button className="ArchiveLibrary__close" onClick={onClose}>&times;</button>
                </div>

                {/* Search */}
                <div className="ArchiveLibrary__search">
                    <i className="fas fa-search ArchiveLibrary__search-icon" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchInput}
                        placeholder="Search archived tasks..."
                        className="ArchiveLibrary__search-input"
                    />
                    {searching && <span className="btn-spinner btn-spinner--dark ArchiveLibrary__search-spinner" />}
                </div>

                {displayedTasks ? (
                    /* Search results */
                    <div className="ArchiveLibrary__body">
                        <div className="ArchiveLibrary__section-title">
                            Search Results ({displayedTasks.length})
                        </div>
                        {displayedTasks.length === 0 ? (
                            <div className="ArchiveLibrary__empty-hint">No archived tasks match your search</div>
                        ) : (
                            <div className="ArchiveLibrary__task-list">
                                {displayedTasks.map(renderTaskItem)}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Tabs */}
                        <div className="ArchiveLibrary__tabs">
                            <button
                                className={`ArchiveLibrary__tab ${activeTab === 'singles' ? 'ArchiveLibrary__tab--active' : ''}`}
                                onClick={() => setActiveTab('singles')}
                            >
                                Singles ({singles.length})
                            </button>
                            <button
                                className={`ArchiveLibrary__tab ${activeTab === 'labels' ? 'ArchiveLibrary__tab--active' : ''}`}
                                onClick={() => setActiveTab('labels')}
                            >
                                Label Groups ({labelGroups.length})
                            </button>
                            <button
                                className={`ArchiveLibrary__tab ${activeTab === 'custom' ? 'ArchiveLibrary__tab--active' : ''}`}
                                onClick={() => setActiveTab('custom')}
                            >
                                Custom Groups ({customGroups.length})
                            </button>
                        </div>

                        <div className="ArchiveLibrary__body">
                            {loading ? (
                                <div className="ArchiveLibrary__loading">
                                    <span className="btn-spinner btn-spinner--dark" /> Loading archive...
                                </div>
                            ) : (
                                <>
                                    {activeTab === 'singles' && (
                                        singles.length === 0 ? (
                                            <div className="ArchiveLibrary__empty-hint">No single archived tasks</div>
                                        ) : (
                                            <div className="ArchiveLibrary__task-list">
                                                {singles.map(renderTaskItem)}
                                            </div>
                                        )
                                    )}

                                    {activeTab === 'labels' && (
                                        labelGroups.length === 0 ? (
                                            <div className="ArchiveLibrary__empty-hint">No label archive groups</div>
                                        ) : (
                                            <div className="ArchiveLibrary__group-list">
                                                {labelGroups.map(renderGroupItem)}
                                            </div>
                                        )
                                    )}

                                    {activeTab === 'custom' && (
                                        customGroups.length === 0 ? (
                                            <div className="ArchiveLibrary__empty-hint">No custom archive groups</div>
                                        ) : (
                                            <div className="ArchiveLibrary__group-list">
                                                {customGroups.map(renderGroupItem)}
                                            </div>
                                        )
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* Unarchive action bar */}
                {selectedTasks.length > 0 && (
                    <div className="ArchiveLibrary__action-bar">
                        <span>{selectedTasks.length} selected</span>
                        <button
                            className="ArchiveLibrary__unarchive-btn"
                            onClick={handleUnarchive}
                            disabled={unarchiving}
                        >
                            {unarchiving ? 'Unarchiving...' : 'Unarchive Selected'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArchiveLibrary;
